import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useState } from 'react';

import { NetworkDataType } from '@/common/models/NetworkDataTransfers';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  colors,
  Dialog,
  flex1,
  flexCenter,
  flexCenterVertical,
  flexColumn,
  FlexColumn,
  FlexRow,
  font,
  fontFamilies,
  fullSize,
  fullWidth,
  IconButton,
  Input,
  Intent,
  padding,
} from '@toilet-paper-games/ui';

import { pickRandomFromArray } from '@/common/utils/code-gen';
import { UserBanner } from '@/controller/components/UserBanner';
import words from 'friendly-words';
import { Shuffle } from 'iconoir-react';
import {
  LocalStorageKeys,
  useControllerAppModel,
  useControllerNetworkHandler,
} from '../models/ControllerAppModel';

export interface EnterNameProps {}

export const EnterName = observer((_props: EnterNameProps) => {
  const appStore = useControllerAppModel();
  const networkHandler = useControllerNetworkHandler();

  const [input, setInput] = useState(
    appStore.userDisplayName ?? localStorage.getItem(LocalStorageKeys.lastUsedName) ?? '',
  );

  useEffect(() => {
    if (appStore.userDisplayName) {
      setInput(appStore.userDisplayName);
    }
  }, [appStore.userDisplayName]);

  // TODO: Add logic to handle a name not being allowed
  const onFormSubmit = useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      appStore.uiModel.showEnterNameDialog = false;

      appStore.updateDisplayName(input);

      const hostConnection = networkHandler.hostConnection;

      if (!hostConnection) {
        throw new Error('No host connection found');
      }

      hostConnection.send({
        type: NetworkDataType.UPDATE_USER_NAME,
        data: {
          screenName: input.trim(),
        },
      });
    },
    [appStore, networkHandler, input],
  );

  const onInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target;
    const cursorPosition = input.selectionStart || 0;
    let newValue = input.value.trimStart();

    newValue = newValue.replace(/\s+/g, ' ');

    setInput(newValue);

    const adjustedCursorPosition = Math.min(cursorPosition, newValue.length);

    setTimeout(() => {
      input.setSelectionRange(adjustedCursorPosition, adjustedCursorPosition);
    }, 0);
  }, []);

  useEffect(() => {
    const hostConnection = networkHandler.hostConnection;

    if (!hostConnection) {
      throw new Error('No host connection found');
    }

    hostConnection.send({
      type: NetworkDataType.UPDATE_USER_NAME,
      data: {
        screenName: input.trim(),
      },
    });
  }, [input, networkHandler]);

  return (
    <Dialog open={appStore.uiModel.showEnterNameDialog} cardProps={{ noPadding: true }}>
      <Card noPadding>
        <CardHeader> Enter Name</CardHeader>
        <CardBody>
          <FlexColumn css={[flexCenter, fullSize]} gap={30}>
            <UserBanner />
            <form
              onSubmit={onFormSubmit}
              css={[fullWidth, flexColumn, flexCenterVertical]}
            >
              <FlexColumn gap={15}>
                <FlexRow css={[fullWidth]} gap={5} alignItems="center">
                  <Input
                    placeholder="Nickname"
                    type="text"
                    onChange={onInputChange}
                    value={input}
                    autoComplete="off"
                    css={[flex1, padding(8), font(fontFamilies.secondary, 24), fullWidth]}
                  />
                  <IconButton
                    noIconAnimation
                    css={{
                      width: 50,
                      height: 50,
                    }}
                    type="button"
                    onClick={() => setInput(pickRandomName())}
                    icon={<Shuffle strokeWidth={2.5} />}
                    color={colors.component}
                  />
                </FlexRow>

                <Button
                  type="submit"
                  intent={Intent.Success}
                  disabled={!input}
                  css={[padding(10)]}
                >
                  Confirm Name
                </Button>
              </FlexColumn>
            </form>
          </FlexColumn>
        </CardBody>
      </Card>
    </Dialog>
  );
});

const pickRandomName = () => pickRandomFromArray(words.objects);
